import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiXCircle, FiArrowLeft } from "react-icons/fi";

const PaymentErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
`;

const ErrorCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f8d7da;
  color: #721c24;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2.5rem;
`;

const ErrorTitle = styled.h1`
  color: #2d5016;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const ErrorDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #2d5016;
`;

const DetailValue = styled.span`
  color: #666;
  font-family: monospace;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #2d5016;
    color: white;

    &:hover {
      background: #1e3a0f;
      transform: translateY(-2px);
    }
  }

  &.secondary {
    background: #6c757d;
    color: white;

    &:hover {
      background: #545b62;
      transform: translateY(-2px);
    }
  }
`;

const PaymentError = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const errorCode = searchParams.get("error_code");
  const errorMessage = searchParams.get("error_message");
  const transactionId = searchParams.get("transaction_id");

  const getErrorMessage = () => {
    switch (errorCode) {
      case "1":
        return "Agent Not Registered - The user is not registered with MMG.";
      case "2":
        return "Payment Failed - The payment could not be processed.";
      case "3":
        return "Invalid Secret Key - There was an issue with the payment configuration.";
      case "4":
        return "Merchant ID Mismatch - Payment configuration error.";
      case "5":
        return "Token Decryption Failed - Invalid payment token.";
      case "6":
        return "Transaction Cancelled - You cancelled the payment.";
      case "7":
        return "Request Timed Out - The payment request timed out.";
      default:
        return (
          errorMessage ||
          "An unexpected error occurred during payment processing."
        );
    }
  };

  const handleRetry = () => {
    navigate("/meal-plans");
  };

  const handleContactSupport = () => {
    // In a real app, this would open a support form or email
    window.location.href =
      "mailto:support@nourishgy.com?subject=Payment Error Support";
  };

  return (
    <PaymentErrorContainer>
      <ErrorCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ErrorIcon>
          <FiXCircle />
        </ErrorIcon>

        <ErrorTitle>Payment Error</ErrorTitle>

        <ErrorMessage>{getErrorMessage()}</ErrorMessage>

        {(errorCode || transactionId) && (
          <ErrorDetails>
            {errorCode && (
              <DetailRow>
                <DetailLabel>Error Code:</DetailLabel>
                <DetailValue>{errorCode}</DetailValue>
              </DetailRow>
            )}
            {transactionId && (
              <DetailRow>
                <DetailLabel>Transaction ID:</DetailLabel>
                <DetailValue>{transactionId}</DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Time:</DetailLabel>
              <DetailValue>{new Date().toLocaleString()}</DetailValue>
            </DetailRow>
          </ErrorDetails>
        )}

        <ActionButtons>
          <Button className="primary" onClick={handleRetry}>
            <FiArrowLeft />
            Try Again
          </Button>
          <Button className="secondary" onClick={handleContactSupport}>
            Contact Support
          </Button>
        </ActionButtons>
      </ErrorCard>
    </PaymentErrorContainer>
  );
};

export default PaymentError;
